// client/indexedDBStore.ts
import { logger } from "@adornis/base/logging.js";
var INDEXEDDB_VERSION = 3;
var IndexedDBBlobStore = class {
  constructor(dbName, storeName) {
    this.storeName = storeName;
    this.failed = false;
    const dbRequest = window.indexedDB.open(dbName, INDEXEDDB_VERSION);
    dbRequest.onerror = () => {
      this.failed = true;
      logger.debug({ dbName }, "IndexedDB Database failed to open");
    };
    dbRequest.onsuccess = () => {
      this.db = dbRequest.result;
      logger.debug({ dbName }, "IndexedDB Database opened successfully");
    };
    dbRequest.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (e.oldVersion === 0) {
        const store = db.createObjectStore(storeName, { keyPath: "key" });
        store.createIndex("blob", "blob", { unique: false });
        store.createIndex("id", "id", { unique: false });
        store.createIndex("index", "index", { unique: false });
        store.createIndex("part", ["id", "part"], { unique: false });
      } else if (e.oldVersion === 1) {
        const store = dbRequest.transaction?.objectStore(storeName);
        if (!store)
          return;
        store.createIndex("id", "id", { unique: false });
        store.createIndex("index", "index", { unique: false });
        store.createIndex("part", ["id", "part"], { unique: false });
      } else if (e.oldVersion === 2) {
        const store = dbRequest.transaction?.objectStore(storeName);
        if (!store)
          return;
        store.createIndex("part", ["id", "part"], { unique: false });
      }
      logger.debug({ dbName }, "IndexedDB Database setup complete");
    };
  }
  getStore(mode) {
    if (this.failed)
      throw new Error("IndexedDBStore failed to initialize");
    if (!this.db)
      throw new Error("IndexedDBStore not yet ready");
    return this.db.transaction([this.storeName], mode).objectStore(this.storeName);
  }
  wrapRequest(request) {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  async storeBlob(key, blob, { force, index = 0, part = 0 } = {}) {
    const store = this.getStore("readwrite");
    const entry = { key: `${key}_${part}_${index}`, blob, index, id: key, part };
    return this.wrapRequest(force ? store.put(entry) : store.add(entry));
  }
  async getBlob(key, index = 0) {
    const store = this.getStore();
    return this.wrapRequest(store.get([key, index]));
  }
  async getAllBlobs(key) {
    const store = this.getStore();
    const index = store.index("id");
    return this.wrapRequest(index.getAll(key));
  }
  async removeAllBlobs(key) {
    const store = this.getStore("readwrite");
    const index = store.index("id");
    const keys = await this.wrapRequest(index.getAllKeys(key));
    await Promise.all(keys.map((key2) => this.wrapRequest(store.delete(key2))));
  }
  async getNextPart(key) {
    const store = this.getStore();
    const index = store.index("part");
    let lastIndex = 0;
    while (await this.wrapRequest(index.getKey([key, lastIndex])))
      lastIndex++;
    return lastIndex;
  }
  async deleteBlob(key) {
    const store = this.getStore("readwrite");
    return this.wrapRequest(store.delete(key));
  }
  async getAllKeys() {
    const store = this.getStore();
    return this.wrapRequest(store.getAllKeys());
  }
};
export {
  IndexedDBBlobStore
};
